import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive, ArchiveRestore, ArrowLeft, Building2, Download, FileText, Globe,
  History, Mail, MapPin, Pencil, Phone, Plus, Save, ShieldAlert, Star, Trash2, Upload, Users, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const ANY = "__any__";

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-muted text-muted-foreground border-border",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
  Blacklisted: "bg-red-50 text-red-700 border-red-200",
};

const NOTE_KINDS = [
  { key: "general", label: "General Notes" },
  { key: "special_instructions", label: "Special Instructions" },
  { key: "escalation", label: "Escalation Notes" },
  { key: "risk", label: "Risk Notes" },
];

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);
const ReadValue = ({ value }) => (
  <p className="text-sm font-medium min-h-[20px] break-words">{Array.isArray(value) ? (value.length ? value.join(", ") : "—") : (value ?? "—") || "—"}</p>
);
const MasterSelect = ({ value, onChange, options, testId }) => (
  <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? null : v)}>
    <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ANY}>Not set</SelectItem>
      {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
const MultiSelect = ({ values = [], onChange, options, testId }) => (
  <div className="space-y-2">
    <Select value={ANY} onValueChange={(v) => { if (v !== ANY && !values.includes(v)) onChange([...values, v]); }}>
      <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="Add..." /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>Add...</SelectItem>
        {(options || []).filter((o) => !values.includes(o)).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => (
        <Badge key={v} variant="outline" className="rounded-full text-xs bg-primary/5 border-primary/20 text-primary gap-1">
          {v}<button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}><X className="h-3 w-3" /></button>
        </Badge>
      ))}
    </div>
  </div>
);

const EMPTY_OFFICE = { office_name: "", address: "", landmark: "", city: "", state: "", pincode: "", maps_link: "", office_type: "", is_head_office: false, active: true };
const EMPTY_CONTACT = { name: "", designation: "", department: "", mobile: "", alt_mobile: "", email: "", whatsapp: "", preferred_communication: "", contact_type: "", active: true };

export default function CompanyProfile() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const backPath = user.role === "recruiter" ? "/recruiter/companies" : "/admin/companies";

  const [company, setCompany] = useState(null);
  const [master, setMaster] = useState({});
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editBasic, setEditBasic] = useState(false);
  const [basicForm, setBasicForm] = useState({});
  const [prefsForm, setPrefsForm] = useState(null);
  const [officeDialog, setOfficeDialog] = useState(null); // {mode:'add'|'edit', data}
  const [contactDialog, setContactDialog] = useState(null);
  const [noteKind, setNoteKind] = useState("general");
  const [noteText, setNoteText] = useState("");
  const [docForm, setDocForm] = useState({ doc_name: "", category: "", remarks: "", doc_group_id: null });
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const docFileRef = useRef(null);
  const logoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get("/master-data"),
      ]);
      setCompany(c.data);
      setBasicForm(c.data);
      setPrefsForm(c.data.hiring_preferences || {});
      setMaster(m.data);
      if (c.data.can_manage) {
        api.get(`/companies/${companyId}/documents`).then((r) => setDocuments(r.data)).catch(() => {});
        api.get(`/companies/${companyId}/activity`).then((r) => setActivity(r.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const saveBasic = async () => {
    setBusy(true);
    try {
      const fields = {};
      ["name", "display_name", "industry", "company_type", "website", "email", "description", "gst_number", "pan_number", "status"].forEach((k) => { fields[k] = basicForm[k] || null; });
      fields.is_premium = !!basicForm.is_premium;
      const res = await api.put(`/companies/${companyId}`, { fields });
      setCompany(res.data);
      setBasicForm(res.data);
      setEditBasic(false);
      toast.success("Company updated");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveOffice = async () => {
    const { mode, data } = officeDialog;
    if (!data.office_name || data.office_name.length < 2) { toast.error("Enter the office name"); return; }
    setBusy(true);
    try {
      if (mode === "add") await api.post(`/companies/${companyId}/offices`, data);
      else await api.put(`/companies/${companyId}/offices/${data.id}`, data);
      toast.success(mode === "add" ? "Office added" : "Office updated");
      setOfficeDialog(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const saveContact = async () => {
    const { mode, data } = contactDialog;
    if (!data.name || data.name.length < 2) { toast.error("Enter the contact name"); return; }
    setBusy(true);
    try {
      if (mode === "add") await api.post(`/companies/${companyId}/contacts`, data);
      else await api.put(`/companies/${companyId}/contacts/${data.id}`, data);
      toast.success(mode === "add" ? "Contact added" : "Contact updated");
      setContactDialog(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const savePrefs = async () => {
    setBusy(true);
    try {
      await api.put(`/companies/${companyId}/hiring-preferences`, {
        hiring_locations: prefsForm.hiring_locations || [],
        preferred_qualification: prefsForm.preferred_qualification || null,
        preferred_experience: prefsForm.preferred_experience || null,
        interview_mode: prefsForm.interview_mode || null,
        working_days: prefsForm.working_days || null,
        shift_type: prefsForm.shift_type || null,
      });
      toast.success("Hiring preferences saved");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) { toast.error("Write the note first"); return; }
    try {
      await api.post(`/companies/${companyId}/notes`, { kind: noteKind, text: noteText.trim() });
      toast.success("Note added — notes are permanent");
      setNoteText("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    if (!docForm.doc_name.trim() || !docForm.category) { toast.error("Enter document name and category"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Document must be 10 MB or smaller"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_name", docForm.doc_name.trim());
    fd.append("category", docForm.category);
    if (docForm.remarks) fd.append("remarks", docForm.remarks);
    if (docForm.doc_group_id) fd.append("doc_group_id", docForm.doc_group_id);
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(docForm.doc_group_id ? "New version uploaded — marked Active" : "Document uploaded");
      setDocDialogOpen(false);
      setDocForm({ doc_name: "", category: "", remarks: "", doc_group_id: null });
      api.get(`/companies/${companyId}/documents`).then((r) => setDocuments(r.data));
      api.get(`/companies/${companyId}/activity`).then((r) => setActivity(r.data));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
      if (docFileRef.current) docFileRef.current.value = "";
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/companies/${companyId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const deleteDocVersion = async (d) => {
    if (!window.confirm(`Delete '${d.doc_name}' v${d.version} permanently? This is logged.`)) return;
    try {
      await api.delete(`/companies/${companyId}/documents/${d.id}`);
      toast.success("Version deleted (logged in activity)");
      api.get(`/companies/${companyId}/documents`).then((r) => setDocuments(r.data));
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2 MB or smaller"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/companies/${companyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
    if (logoRef.current) logoRef.current.value = "";
  };

  const toggleArchive = async () => {
    try {
      await api.post(`/companies/${companyId}/${company.archived ? "restore" : "archive"}`);
      toast.success(company.archived ? "Company restored" : "Company archived (soft — restorable anytime)");
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-96 rounded-xl" /></div>;
  if (!company) return <p className="text-sm text-muted-foreground">Company not found.</p>;

  const offices = company.offices || [];
  const contacts = company.contacts || [];
  const notes = [...(company.internal_notes || [])].reverse();
  const logoUrl = company.logo_filename ? `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/logo` : null;

  return (
    <div className="space-y-5" data-testid="company-profile-page">
      <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="company-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to companies
      </Link>

      {/* Header */}
      <Card className="rounded-xl border-border shadow-none">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <img src={logoUrl} alt={`${company.name} logo`} className="h-12 w-12 rounded-lg object-contain border border-border bg-white shrink-0" />
              ) : (
                <span className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Building2 className="h-6 w-6" /></span>
              )}
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-heading font-semibold" data-testid="company-profile-name">{company.display_name || company.name}</h1>
                  <Badge variant="outline" className="rounded-full font-mono text-[10px]" data-testid="company-profile-code">{company.code}</Badge>
                  <Badge variant="outline" className={`rounded-full text-[10px] ${STATUS_STYLES[company.status] || ""}`} data-testid="company-status-badge">{company.status}</Badge>
                  {company.is_premium && <Badge variant="outline" className="rounded-full text-[10px] bg-amber-100 text-amber-800 border-amber-300" data-testid="company-premium-badge">Premium</Badge>}
                  {company.archived && <Badge className="rounded-full text-[10px] bg-slate-700 text-white" data-testid="company-archived-badge">Archived</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {company.industry && <span>{company.industry}</span>}
                  {company.website && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{company.website}</span>}
                  {company.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{company.email}</span>}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              {isAdmin && (
                <div className="flex flex-wrap gap-2">
                  <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="company-logo-input" />
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => logoRef.current?.click()} data-testid="company-logo-button">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Logo
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={toggleArchive} data-testid="company-archive-button">
                    {company.archived ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore</> : <><Archive className="h-3.5 w-3.5 mr-1.5" /> Archive</>}
                  </Button>
                </div>
              )}
              <div className="w-full sm:w-56">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Profile completion</span>
                  <span className="font-medium text-foreground" data-testid="company-completion-percent">{company.profile_completion}%</span>
                </div>
                <Progress value={company.profile_completion} className="h-1.5" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="basic" data-testid="tab-basic">Basic Info</TabsTrigger>
          <TabsTrigger value="offices" data-testid="tab-offices">Offices ({offices.filter((o) => o.active !== false).length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">HR Contacts ({contacts.filter((c) => c.active !== false).length})</TabsTrigger>
          <TabsTrigger value="prefs" data-testid="tab-prefs">Hiring Preferences</TabsTrigger>
          {isAdmin && <TabsTrigger value="documents" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>}
          {isAdmin && <TabsTrigger value="notes" data-testid="tab-notes">Internal Notes ({notes.length})</TabsTrigger>}
          {isAdmin && <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>}
        </TabsList>

        {/* BASIC */}
        <TabsContent value="basic">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-heading">Basic Information</CardTitle>
              {isAdmin && !editBasic && <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setEditBasic(true)} data-testid="company-edit-basic"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>}
              {editBasic && (
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-lg" onClick={saveBasic} disabled={busy} data-testid="company-save-basic"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button>
                  <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => { setBasicForm(company); setEditBasic(false); }}>Cancel</Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company name">{editBasic ? <Input data-testid="basic-name" value={basicForm.name || ""} onChange={(e) => setBasicForm({ ...basicForm, name: e.target.value })} /> : <ReadValue value={company.name} />}</Field>
                <Field label="Display name">{editBasic ? <Input data-testid="basic-display-name" value={basicForm.display_name || ""} onChange={(e) => setBasicForm({ ...basicForm, display_name: e.target.value })} /> : <ReadValue value={company.display_name} />}</Field>
                <Field label="Industry">{editBasic ? <MasterSelect testId="basic-industry" value={basicForm.industry} onChange={(v) => setBasicForm({ ...basicForm, industry: v })} options={master.industry} /> : <ReadValue value={company.industry} />}</Field>
                <Field label="Company type">{editBasic ? <MasterSelect testId="basic-type" value={basicForm.company_type} onChange={(v) => setBasicForm({ ...basicForm, company_type: v })} options={master.company_type} /> : <ReadValue value={company.company_type} />}</Field>
                <Field label="Website">{editBasic ? <Input data-testid="basic-website" value={basicForm.website || ""} onChange={(e) => setBasicForm({ ...basicForm, website: e.target.value })} /> : <ReadValue value={company.website} />}</Field>
                <Field label="Company email">{editBasic ? <Input data-testid="basic-email" value={basicForm.email || ""} onChange={(e) => setBasicForm({ ...basicForm, email: e.target.value })} /> : <ReadValue value={company.email} />}</Field>
                <Field label="GST number">{editBasic ? <Input data-testid="basic-gst" value={basicForm.gst_number || ""} onChange={(e) => setBasicForm({ ...basicForm, gst_number: e.target.value })} /> : <ReadValue value={company.gst_number} />}</Field>
                <Field label="PAN number">{editBasic ? <Input data-testid="basic-pan" value={basicForm.pan_number || ""} onChange={(e) => setBasicForm({ ...basicForm, pan_number: e.target.value })} /> : <ReadValue value={company.pan_number} />}</Field>
                <Field label="Status">{editBasic ? <MasterSelect testId="basic-status" value={basicForm.status} onChange={(v) => setBasicForm({ ...basicForm, status: v })} options={master.company_status} /> : <ReadValue value={company.status} />}</Field>
                <Field label="Premium">
                  {editBasic ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={!!basicForm.is_premium}
                              onCheckedChange={(v) => setBasicForm({ ...basicForm, is_premium: v })}
                              data-testid="basic-premium-toggle" />
                      <span className="text-sm">{basicForm.is_premium ? "Premium" : "Non-premium"}</span>
                    </label>
                  ) : (
                    <ReadValue value={company.is_premium ? "Premium" : "Non-premium"} />
                  )}
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Description">{editBasic ? <Textarea data-testid="basic-description" rows={3} value={basicForm.description || ""} onChange={(e) => setBasicForm({ ...basicForm, description: e.target.value })} /> : <ReadValue value={company.description} />}</Field>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1 mt-5 pt-4 border-t border-border text-xs text-muted-foreground">
                <span>Created: {formatDateTime(company.created_at)} by {company.created_by?.name || "—"}</span>
                <span>Updated: {formatDateTime(company.updated_at)} by {company.updated_by?.name || "—"}</span>
                <span>Last activity: {formatDateTime(company.last_activity_at)}</span>
                <span>Total jobs: {company.total_jobs} · Active jobs: {company.active_jobs}</span>
                <span>Total applications: {company.total_applications}</span>
                <span>Joined candidates: {company.total_joined}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OFFICES */}
        <TabsContent value="offices">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Office Locations</CardTitle>
              {isAdmin && <Button size="sm" className="rounded-lg" onClick={() => setOfficeDialog({ mode: "add", data: { ...EMPTY_OFFICE } })} data-testid="office-add-button"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Office</Button>}
            </CardHeader>
            <CardContent className="space-y-3">
              {offices.length === 0 && <p className="text-sm text-muted-foreground">No offices added yet.</p>}
              {offices.map((o) => (
                <div key={o.id} className={`rounded-lg border p-3.5 ${o.active === false ? "opacity-60" : ""} border-border`} data-testid="office-item">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2">
                        {o.office_name}
                        {o.is_head_office && <Badge className="rounded-full text-[9px] bg-primary text-white"><Star className="h-2.5 w-2.5 mr-0.5" /> Head Office</Badge>}
                        {o.office_type && <span className="text-[10px] text-muted-foreground font-normal">{o.office_type}</span>}
                        {o.active === false && <Badge variant="outline" className="rounded-full text-[9px]">Inactive</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {[o.address, o.landmark, o.city, o.state, o.pincode].filter(Boolean).join(", ") || "Address not set"}
                      </p>
                      {o.maps_link && <a href={o.maps_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Open in Google Maps</a>}
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOfficeDialog({ mode: "edit", data: { ...o } })} aria-label="Edit office" data-testid="office-edit-button">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> HR Contacts <span className="text-[10px] font-normal text-muted-foreground">(never deleted — mark inactive instead)</span></CardTitle>
              {isAdmin && <Button size="sm" className="rounded-lg" onClick={() => setContactDialog({ mode: "add", data: { ...EMPTY_CONTACT } })} data-testid="contact-add-button"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Contact</Button>}
            </CardHeader>
            <CardContent className="space-y-3">
              {contacts.length === 0 && <p className="text-sm text-muted-foreground">No HR contacts added yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className={`rounded-lg border p-3.5 border-border ${c.active === false ? "opacity-60" : ""}`} data-testid="contact-item">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2">
                        {c.name}
                        {c.contact_type && <Badge variant="outline" className={`rounded-full text-[9px] ${(c.contact_type || "").startsWith("Primary") ? "bg-primary/10 text-primary border-primary/30" : ""}`}>{c.contact_type}</Badge>}
                        {c.active === false && <Badge variant="outline" className="rounded-full text-[9px]">Inactive</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{[c.designation, c.department].filter(Boolean).join(" · ")}</p>
                      <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {c.mobile && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /><span className="font-mono">{c.mobile}</span></span>}
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                        {c.whatsapp && <span>WA: {c.whatsapp}</span>}
                        {c.preferred_communication && <span>Prefers: {c.preferred_communication}</span>}
                      </p>
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setContactDialog({ mode: "edit", data: { ...c } })} aria-label="Edit contact" data-testid="contact-edit-button">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREFS */}
        <TabsContent value="prefs">
          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading">Default Hiring Preferences</CardTitle>
              <p className="text-xs text-muted-foreground">Used as defaults when creating jobs for this company (overridable per job in the future Job Master).</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {prefsForm && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Hiring locations">
                      {isAdmin ? <MultiSelect testId="prefs-locations" values={prefsForm.hiring_locations || []} onChange={(v) => setPrefsForm({ ...prefsForm, hiring_locations: v })} options={master.preferred_location} /> : <ReadValue value={company.hiring_preferences?.hiring_locations} />}
                    </Field>
                  </div>
                  <Field label="Preferred qualification">{isAdmin ? <MasterSelect testId="prefs-qualification" value={prefsForm.preferred_qualification} onChange={(v) => setPrefsForm({ ...prefsForm, preferred_qualification: v })} options={master.qualification} /> : <ReadValue value={company.hiring_preferences?.preferred_qualification} />}</Field>
                  <Field label="Preferred experience">{isAdmin ? <MasterSelect testId="prefs-experience" value={prefsForm.preferred_experience} onChange={(v) => setPrefsForm({ ...prefsForm, preferred_experience: v })} options={master.experience} /> : <ReadValue value={company.hiring_preferences?.preferred_experience} />}</Field>
                  <Field label="Interview mode">{isAdmin ? <MasterSelect testId="prefs-interview-mode" value={prefsForm.interview_mode} onChange={(v) => setPrefsForm({ ...prefsForm, interview_mode: v })} options={master.interview_mode} /> : <ReadValue value={company.hiring_preferences?.interview_mode} />}</Field>
                  <Field label="Working days">{isAdmin ? <MasterSelect testId="prefs-working-days" value={prefsForm.working_days} onChange={(v) => setPrefsForm({ ...prefsForm, working_days: v })} options={master.working_days} /> : <ReadValue value={company.hiring_preferences?.working_days} />}</Field>
                  <Field label="Shift type">{isAdmin ? <MasterSelect testId="prefs-shift" value={prefsForm.shift_type} onChange={(v) => setPrefsForm({ ...prefsForm, shift_type: v })} options={master.shift} /> : <ReadValue value={company.hiring_preferences?.shift_type} />}</Field>
                </div>
              )}
              {isAdmin && <Button onClick={savePrefs} disabled={busy} className="rounded-lg" data-testid="prefs-save-button"><Save className="h-4 w-4 mr-1.5" /> Save Preferences</Button>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOCUMENTS (admin only) */}
        {isAdmin && (
          <TabsContent value="documents">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Documents <span className="text-[10px] font-normal text-muted-foreground">(admin only)</span></CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">PDF/DOC/DOCX/XLS/XLSX/PNG/JPG · max 10 MB · versioned — old versions are never overwritten.</p>
                </div>
                <Button size="sm" className="rounded-lg" onClick={() => { setDocForm({ doc_name: "", category: "", remarks: "", doc_group_id: null }); setDocDialogOpen(true); }} data-testid="document-add-button">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Upload Document
                </Button>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {documents.length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>}
                {documents.map((d) => (
                  <div key={d.id} className={`flex items-center gap-2 rounded-lg border border-border px-3 py-2 ${d.archived ? "opacity-60" : ""}`} data-testid="document-item">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{d.doc_name} <span className="text-muted-foreground font-normal">· {d.category}</span></p>
                      <p className="text-[10px] text-muted-foreground">v{d.version} · {d.filename} · {(d.size / 1024).toFixed(0)} KB · {formatDate(d.uploaded_at)} · by {d.uploaded_by?.name}{d.remarks ? ` · ${d.remarks}` : ""}</p>
                    </div>
                    {d.active && <Badge className="rounded-full text-[9px] bg-emerald-600 text-white shrink-0">Active</Badge>}
                    {d.archived && <Badge variant="outline" className="rounded-full text-[9px] shrink-0">Archived</Badge>}
                    <Button variant="ghost" size="sm" className="h-7 rounded-lg text-[10px] shrink-0" onClick={() => { setDocForm({ doc_name: d.doc_name, category: d.category, remarks: "", doc_group_id: d.doc_group_id }); setDocDialogOpen(true); }} data-testid="document-new-version-button">
                      + Version
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => downloadDoc(d)} aria-label="Download" data-testid="document-download-button"><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => api.post(`/companies/${companyId}/documents/${d.id}/archive?archived=${!d.archived}`).then(() => api.get(`/companies/${companyId}/documents`).then((r) => setDocuments(r.data)))} aria-label="Archive" data-testid="document-archive-button"><Archive className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-red-600" onClick={() => deleteDocVersion(d)} aria-label="Delete version" data-testid="document-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* NOTES (admin only) */}
        {isAdmin && (
          <TabsContent value="notes">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-heading flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Internal Notes <span className="text-[10px] font-normal text-muted-foreground">(admin only · append-only — old notes can never be edited or deleted)</span></CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={noteKind} onValueChange={setNoteKind}>
                    <SelectTrigger className="h-9 sm:w-52" data-testid="note-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NOTE_KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Textarea rows={2} className="flex-1" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a permanent note..." data-testid="note-text-input" />
                  <Button onClick={addNote} className="rounded-lg" data-testid="note-add-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2.5">
                  {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
                  {notes.map((n) => (
                    <div key={n.id} className="rounded-lg border border-border p-3" data-testid="note-item">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="rounded-full text-[9px]">{NOTE_KINDS.find((k) => k.key === n.kind)?.label || n.kind}</Badge>
                        <span className="text-[10px] text-muted-foreground">{n.by?.name} · {formatDateTime(n.at)}</span>
                      </div>
                      <p className="text-sm mt-1.5 whitespace-pre-wrap">{n.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ACTIVITY (admin only) */}
        {isAdmin && (
          <TabsContent value="activity">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Activity History ({activity.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-5 space-y-4 max-h-[480px] overflow-y-auto">
                  <span className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />
                  {activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
                  {activity.map((a) => (
                    <div key={a.id} className="relative" data-testid="activity-item">
                      <span className="absolute -left-[19px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                      <p className="text-xs font-medium">{(a.action || "").replaceAll("_", " ")}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{a.details}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{a.actor_name || "System"} · {formatDateTime(a.created_at)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Office dialog */}
      <Dialog open={!!officeDialog} onOpenChange={(o) => !o && setOfficeDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="office-dialog">
          <DialogHeader><DialogTitle>{officeDialog?.mode === "add" ? "Add office" : "Edit office"}</DialogTitle></DialogHeader>
          {officeDialog && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              <Field label="Office name *"><Input data-testid="office-name-input" value={officeDialog.data.office_name} onChange={(e) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, office_name: e.target.value } })} /></Field>
              <Field label="Address"><Textarea rows={2} data-testid="office-address-input" value={officeDialog.data.address || ""} onChange={(e) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, address: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Landmark"><Input value={officeDialog.data.landmark || ""} onChange={(e) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, landmark: e.target.value } })} /></Field>
                <Field label="PIN code"><Input data-testid="office-pincode-input" value={officeDialog.data.pincode || ""} onChange={(e) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, pincode: e.target.value } })} /></Field>
                <Field label="City"><MasterSelect testId="office-city-select" value={officeDialog.data.city} onChange={(v) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, city: v } })} options={master.city} /></Field>
                <Field label="State"><MasterSelect testId="office-state-select" value={officeDialog.data.state} onChange={(v) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, state: v } })} options={master.state} /></Field>
                <Field label="Office type"><MasterSelect testId="office-type-select" value={officeDialog.data.office_type} onChange={(v) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, office_type: v } })} options={master.office_type} /></Field>
                <Field label="Google Maps link"><Input value={officeDialog.data.maps_link || ""} onChange={(e) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, maps_link: e.target.value } })} /></Field>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch checked={officeDialog.data.is_head_office} onCheckedChange={(v) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, is_head_office: v } })} data-testid="office-head-switch" />
                  Head Office
                </label>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch checked={officeDialog.data.active !== false} onCheckedChange={(v) => setOfficeDialog({ ...officeDialog, data: { ...officeDialog.data, active: v } })} data-testid="office-active-switch" />
                  Active
                </label>
              </div>
              <Button onClick={saveOffice} disabled={busy} className="w-full rounded-lg" data-testid="office-save-button">{busy ? "Saving..." : "Save Office"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact dialog */}
      <Dialog open={!!contactDialog} onOpenChange={(o) => !o && setContactDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="contact-dialog">
          <DialogHeader><DialogTitle>{contactDialog?.mode === "add" ? "Add HR contact" : "Edit HR contact"}</DialogTitle></DialogHeader>
          {contactDialog && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *"><Input data-testid="contact-name-input" value={contactDialog.data.name} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, name: e.target.value } })} /></Field>
                <Field label="Designation"><Input data-testid="contact-designation-input" value={contactDialog.data.designation || ""} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, designation: e.target.value } })} /></Field>
                <Field label="Department"><MasterSelect testId="contact-department-select" value={contactDialog.data.department} onChange={(v) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, department: v } })} options={master.department} /></Field>
                <Field label="Contact type"><MasterSelect testId="contact-type-select" value={contactDialog.data.contact_type} onChange={(v) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, contact_type: v } })} options={master.contact_type} /></Field>
                <Field label="Mobile"><Input data-testid="contact-mobile-input" maxLength={10} value={contactDialog.data.mobile || ""} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, mobile: e.target.value.replace(/\D/g, "") } })} /></Field>
                <Field label="Alternate mobile"><Input maxLength={10} value={contactDialog.data.alt_mobile || ""} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, alt_mobile: e.target.value.replace(/\D/g, "") } })} /></Field>
                <Field label="Email"><Input data-testid="contact-email-input" type="email" value={contactDialog.data.email || ""} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, email: e.target.value } })} /></Field>
                <Field label="WhatsApp number"><Input maxLength={10} value={contactDialog.data.whatsapp || ""} onChange={(e) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, whatsapp: e.target.value.replace(/\D/g, "") } })} /></Field>
                <Field label="Preferred communication"><MasterSelect testId="contact-comm-select" value={contactDialog.data.preferred_communication} onChange={(v) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, preferred_communication: v } })} options={master.communication_method} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={contactDialog.data.active !== false} onCheckedChange={(v) => setContactDialog({ ...contactDialog, data: { ...contactDialog.data, active: v } })} data-testid="contact-active-switch" />
                Active {contactDialog.mode === "edit" && <span className="text-[10px] text-muted-foreground font-normal">(contacts are never deleted — turn off instead)</span>}
              </label>
              <Button onClick={saveContact} disabled={busy} className="w-full rounded-lg" data-testid="contact-save-button">{busy ? "Saving..." : "Save Contact"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document upload dialog */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent data-testid="document-dialog">
          <DialogHeader><DialogTitle>{docForm.doc_group_id ? `New version — ${docForm.doc_name}` : "Upload document"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Document name *"><Input data-testid="document-name-input" value={docForm.doc_name} disabled={!!docForm.doc_group_id} onChange={(e) => setDocForm({ ...docForm, doc_name: e.target.value })} placeholder="e.g. Service Agreement 2026" /></Field>
            <Field label="Category *">
              <Select value={docForm.category || ANY} onValueChange={(v) => setDocForm({ ...docForm, category: v === ANY ? "" : v })} disabled={!!docForm.doc_group_id && !!docForm.category}>
                <SelectTrigger data-testid="document-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Select category</SelectItem>
                  {(master.document_type || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Remarks (optional)"><Input data-testid="document-remarks-input" value={docForm.remarks} onChange={(e) => setDocForm({ ...docForm, remarks: e.target.value })} /></Field>
            <input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} data-testid="document-file-input" />
            <Button className="w-full rounded-lg" disabled={busy} onClick={() => docFileRef.current?.click()} data-testid="document-choose-file">
              <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading..." : "Choose file & upload"}
            </Button>
            <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, XLS, XLSX, PNG, JPG · max 10 MB. Uploading a new version never overwrites the old one.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
